/**
 * Two-sided hourly tariff: what a kWh costs coming in, what it earns going out.
 *
 * Dutch retail electricity is asymmetric. A kWh taken from the grid costs the
 * commodity price plus energy tax plus the supplier's markup, with VAT on top
 * of all of it. A kWh fed back earns the commodity price minus that markup, and
 * where a supplier charges feed-in costs it can earn less than nothing. The gap
 * between the two sides is what a battery monetises, so valuing both legs of a
 * cycle at one price makes self-consumption look worthless and grid arbitrage
 * look profitable — both wrong, in opposite directions.
 */

/** Energy tax on electricity, €/kWh excl. VAT. Statutory; revisit every year. */
export const ENERGY_TAX_EUR_PER_KWH = 0.10154
/** Supplier markup ("opslag") on a dynamic contract, €/kWh excl. VAT. */
export const SUPPLIER_MARKUP_EUR_PER_KWH = 0.02
export const VAT_RATE = 0.21
/**
 * Feed-in costs ("terugleverkosten") per exported kWh. Suppliers differ wildly
 * and many charge nothing, so the default stays at zero: we would rather miss
 * an upside than invent one.
 */
export const DEFAULT_FEED_IN_FEE_EUR_PER_KWH = 0

export interface HourlyTariff {
  /** All-in €/kWh paid for a kWh drawn from the grid in this hour. */
  importEurPerKwh(time: string): number
  /** Net €/kWh received for exporting in this hour; negative when fees exceed the price. */
  exportEurPerKwh(time: string): number
  /** Average import price, for energy that has no hour of its own (standby). */
  readonly averageImportEurPerKwh: number
  /** Average gap between the two sides; the headroom a stored kWh can earn. */
  readonly averageSpreadEurPerKwh: number
}

export interface TariffFeeOptions {
  energyTaxEurPerKwh?: number
  supplierMarkupEurPerKwh?: number
  vatRate?: number
  feedInFeeEurPerKwh?: number
}

/**
 * Fixed contract: the household already knows its all-in prices, so the only
 * adjustment is any feed-in fee charged on top of the agreed return rate.
 */
export function fixedTariff(
  importEurPerKwh: number,
  exportEurPerKwh: number,
  options: Pick<TariffFeeOptions, 'feedInFeeEurPerKwh'> = {}
): HourlyTariff {
  const fee = options.feedInFeeEurPerKwh ?? DEFAULT_FEED_IN_FEE_EUR_PER_KWH
  const exportNet = exportEurPerKwh - fee
  return {
    importEurPerKwh: () => importEurPerKwh,
    exportEurPerKwh: () => exportNet,
    averageImportEurPerKwh: importEurPerKwh,
    averageSpreadEurPerKwh: importEurPerKwh - exportNet,
  }
}

/**
 * Dynamic contract, built up from bare day-ahead prices (EUR/kWh incl. VAT on
 * the commodity, as EnergyZero serves them). Hours the price feed does not
 * cover fall back to the period average rather than to zero, which would
 * otherwise read as free energy.
 */
export function spotTariff(
  spotEurPerKwh: Map<string, number>,
  options: TariffFeeOptions = {}
): HourlyTariff {
  const energyTax = options.energyTaxEurPerKwh ?? ENERGY_TAX_EUR_PER_KWH
  const markup = options.supplierMarkupEurPerKwh ?? SUPPLIER_MARKUP_EUR_PER_KWH
  const vat = options.vatRate ?? VAT_RATE
  const fee = options.feedInFeeEurPerKwh ?? DEFAULT_FEED_IN_FEE_EUR_PER_KWH

  const importSurcharge = (energyTax + markup) * (1 + vat)
  const exportDeduction = markup * (1 + vat) + fee

  let sum = 0
  for (const price of spotEurPerKwh.values()) sum += price
  const meanSpot = spotEurPerKwh.size > 0 ? sum / spotEurPerKwh.size : 0
  const spotAt = (time: string) => spotEurPerKwh.get(time) ?? meanSpot

  return {
    importEurPerKwh: (time) => spotAt(time) + importSurcharge,
    exportEurPerKwh: (time) => spotAt(time) - exportDeduction,
    averageImportEurPerKwh: meanSpot + importSurcharge,
    averageSpreadEurPerKwh: importSurcharge + exportDeduction,
  }
}

export interface TariffSource extends TariffFeeOptions {
  /** Explicit tariff wins; otherwise one is derived from the fields below. */
  tariff?: HourlyTariff | null
  /** Bare day-ahead prices keyed by UTC hour ISO. */
  hourlyPricesByTime?: Map<string, number>
  fixedConsumptionPriceEurPerKwh?: number
  fixedReturnPriceEurPerKwh?: number
}

/** Resolve whatever pricing a caller supplied into a two-sided tariff. */
export function resolveTariff(source: TariffSource): HourlyTariff | null {
  if (source.tariff) return source.tariff
  if (source.hourlyPricesByTime && source.hourlyPricesByTime.size > 0) {
    return spotTariff(source.hourlyPricesByTime, source)
  }
  if (
    source.fixedConsumptionPriceEurPerKwh != null &&
    source.fixedReturnPriceEurPerKwh != null
  ) {
    return fixedTariff(
      source.fixedConsumptionPriceEurPerKwh,
      source.fixedReturnPriceEurPerKwh,
      source
    )
  }
  return null
}
